import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Coffee, Bell } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useShopTier } from '../../hooks/useShopTier';
import { useRewardMirrorStatus } from '../../hooks/useRewardMirrorStatus';
import { realtimeClient, type ConnectionState } from '../../services/realtime-client';
import {
  getPendingStampsForShop, approveStampRequest, denyStampRequest,
  type StampRequestRow,
} from '../../services/stamp-service';
import { fetchShopCapabilities, queryRecentPurchases } from '../../services/sui-service';
import { restoreSupabaseSession } from '../../services/supabase-auth';
import { getShopByOwner, countShopPurchases } from '../../services/shop-service';
import GiveawayStats from './GiveawayStats';
import { currencyLabel } from '../../lib/currency-label';
import { formatGeoBadge } from '../../lib/geo-badge';
import { startSquareConnect, getSquareStatus, type SquareStatus } from '../../services/square-service';
import ShopQRCode from './ShopQRCode';
import ScanPassModal from './ScanPassModal';
import CounterBook from './CounterBook';
import OnboardingChecklist from './OnboardingChecklist';
import OwnerShell, { OwnerLoadingShell } from './OwnerShell';
import CoralHeader from '../dashboard/CoralHeader';

function ShopOwnerDashboard() {
  const { userAddress } = useAuthStore();
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState<StampRequestRow[]>([]);
  const [shopCapability, setShopCapability] = useState<any>(null);
  const [shopData, setShopData] = useState<any>(null);
  const [recentPurchases, setRecentPurchases] = useState<any[]>([]);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [wsState, setWsState] = useState<ConnectionState>('disconnected');
  const [requestQuantities, setRequestQuantities] = useState<Record<string, number>>({});
  // Lifetime stamps for this shop, counted server-side. Was previously derived
  // from `recentPurchases.length`, which is a *global* last-10 event page
  // filtered to this shop client-side — so it saturated at 10 and, once a
  // second shop is live, could read 0 for a busy café.
  const [totalStamps, setTotalStamps] = useState<number | null>(null);
  // Optional comp code input per pending stamp request — when filled, the
  // approve call atomically redeems the comp via the redeem_comp_offer RPC.
  const [compCodes, setCompCodes] = useState<Record<string, string>>({});
  const [squareStatus, setSquareStatus] = useState<SquareStatus | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [counterBookOpen, setCounterBookOpen] = useState(false);
  const [squareBusy, setSquareBusy] = useState(false);
  const [squareResult, setSquareResult] = useState<string | null>(null);
  const [billingResult, setBillingResult] = useState<string | null>(null);
  const [dbShopId, setDbShopId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const activeShopOwner = userAddress;

  // OAuth/checkout callbacks land back here with ?square=connected|denied|badstate|error
  // or ?billing=pending. Surface once, then strip the param so refresh doesn't replay it.
  useEffect(() => {
    const squareParam = searchParams.get('square');
    const billingParam = searchParams.get('billing');
    if (!squareParam && !billingParam) return;
    if (squareParam && ['connected', 'denied', 'badstate', 'error'].includes(squareParam)) {
      setSquareResult(squareParam);
    }
    if (billingParam === 'pending') {
      setBillingResult(billingParam);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('square');
    next.delete('billing');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // While a checkout return is pending, poll the tier until the webhook lands
  // (or give up after 2 min — Square retries will still flip it eventually).
  const { data: pendingTierInfo } = useShopTier(billingResult === 'pending' ? dbShopId : null);
  // Reward-mirror staleness — a cheap, tier-agnostic single-query check
  // (fix round 1, Finding 3: NOT useAnalyticsSummary, which is tier-gated
  // founding/pro — a 403+retries no-op for free shops — and scans up to
  // 5000 purchase_events rows; too heavy for the operational tab a barista
  // has open during service). A stale mirror is invisible right up until an
  // RPC outage, at which point verifyFromMirror refuses to authorise off
  // it — the owner should know before that moment, not during it.
  const { data: rewardMirrorStatus } = useRewardMirrorStatus(!!userAddress);
  useEffect(() => {
    if (billingResult !== 'pending' || !dbShopId) return;
    const iv = setInterval(
      () => queryClient.invalidateQueries({ queryKey: ['shop-tier', dbShopId] }),
      5000,
    );
    const stop = setTimeout(() => clearInterval(iv), 120_000);
    return () => { clearInterval(iv); clearTimeout(stop); };
  }, [billingResult, dbShopId, queryClient]);
  useEffect(() => {
    if (billingResult === 'pending' && pendingTierInfo?.billingStatus === 'active') {
      setBillingResult('activated');
    }
  }, [billingResult, pendingTierInfo]);

  // Square connection status (best-effort; null if no shop / not connected).
  useEffect(() => {
    if (!userAddress) return;
    getSquareStatus().then(setSquareStatus).catch(() => setSquareStatus(null));
  }, [userAddress]);

  async function handleSquareConnect() {
    setSquareBusy(true);
    try {
      await startSquareConnect(); // redirects the browser to Square on success
    } catch (e) {
      setSquareBusy(false);
      alert(e instanceof Error ? e.message : 'Square connect failed');
    }
  }

  // Re-fetch recent activity from chain
  const refreshActivity = useCallback(async () => {
    if (!userAddress) return;
    try {
      const recent = await queryRecentPurchases(userAddress, 10);
      setRecentPurchases(recent);
    } catch { /* ignore — non-critical */ }
    if (dbShopId) {
      countShopPurchases(dbShopId).then(setTotalStamps).catch(() => { /* keep last known */ });
    }
  }, [userAddress, dbShopId]);

  // Supabase Realtime connection (barista role).
  // Wait for the Supabase JWT to be restored — both the initial RLS-gated
  // SELECTs and the Realtime subscription require auth, otherwise reads
  // return zero rows and the channel silently drops events.
  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    let unsub: (() => void) | undefined;
    (async () => {
      await restoreSupabaseSession(userAddress);
      if (cancelled) return;

      // Fetch initial pending requests (now authed)
      getPendingStampsForShop(userAddress).then((rows) => {
        if (!cancelled) setPendingRequests(rows.filter((row) => row.status === 'pending'));
      });
      // No pending-redeem fetch: redemptions are settled at the counter by the
      // barista's tap, so there is no queue to prime. GiveawayStats reads the
      // history instead.

      // Subscribe to realtime changes (now authed)
      realtimeClient.subscribeAsBarista(
        userAddress,
        (row) => {
          if (row.status === 'pending') {
            setPendingRequests(prev => [row, ...prev.filter(r => r.id !== row.id)]);
          }
        },
        (row) => {
          setPendingRequests(prev => (
            row.status === 'pending'
              ? prev.map(r => r.id === row.id ? row : r)
              : prev.filter(r => r.id !== row.id)
          ));
          if (row.onchain_status === 'confirmed') refreshActivity();
        },
        // Redeem insert/update: nothing to do. There is no approval queue to
        // keep in sync — the barista's tap settles a redemption server-side in
        // one call. Kept as no-ops rather than changing subscribeAsBarista's
        // signature, which the stamp queue above still needs.
        () => {},
        () => {},
      );

      unsub = realtimeClient.onStateChange(setWsState);
    })();
    return () => { cancelled = true; if (unsub) unsub(); realtimeClient.cleanup(); };
  }, [userAddress, refreshActivity]);

  useEffect(() => {
    if (!userAddress) return;
    loadShopData();
  }, [userAddress]);


  async function loadShopData() {
    if (!userAddress) return;
    setLoading(true);
    try {
      // Capability gives us the on-chain shop_id required for stamping calls.
      // Display name + editable metadata live in Supabase (source of truth).
      const [capabilities, dbShop] = await Promise.all([
        fetchShopCapabilities(userAddress),
        getShopByOwner(userAddress),
      ]);
      setDbShopId(dbShop?.id || null);
      if (dbShop?.id) {
        countShopPurchases(dbShop.id).then(setTotalStamps).catch(() => setTotalStamps(null));
      }
      if (capabilities.length > 0) {
        setShopCapability(capabilities[0]);
        const capFields = (capabilities[0].data?.content as any)?.fields || {};
        setShopData({
          name: dbShop?.name || capFields.shop_name || 'My Shop',
          id: capFields.shop_id,
          image_url: dbShop?.image_url,
          pixel_image_url: dbShop?.pixel_image_url,
          location: dbShop?.location,
          beans: dbShop?.beans,
          stamp_icon: dbShop?.stamp_icon,
          currency_name: dbShop?.currency_name,
        });
        const recent = await queryRecentPurchases(userAddress, 10);
        setRecentPurchases(recent);
      }
    } catch (err) {
      console.error('Failed to load shop data:', err);
    }
    setLoading(false);
  }

  const handleApproveRequest = useCallback(async (request: StampRequestRow) => {
    setApprovingId(request.id);
    const quantity = requestQuantities[request.id] || 1;
    const compCode = compCodes[request.id]?.trim() || undefined;
    try {
      // Signal approval — customer signs the on-chain record_purchase() tx
      const updated = await approveStampRequest(request.id, quantity, compCode);
      if (!updated) {
        // Most common cause: the request expired (pg_cron flips rows after
        // 5 min) or was answered from another device — a 409 either way.
        // Silently doing nothing made the button look broken.
        // eslint-disable-next-line no-alert
        alert('Could not approve — the request probably expired or was already answered. Ask the customer to tap again.');
        setPendingRequests(prev => prev.filter(r => r.id !== request.id));
        return;
      }
      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
      // Optimistic bump so the tile moves the instant the barista approves.
      // `refreshActivity` re-reads the authoritative count 8s later and
      // reconciles, so this can't drift. (Previously this fed a separate
      // counter that was ADDED to the recent-purchases list, double-counting
      // every approval once the purchase landed in that list.)
      setTotalStamps(prev => (prev === null ? prev : prev + quantity));
      // Clear the comp code on success so the input doesn't carry over
      setCompCodes(prev => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });
      // Refresh activity feed after a short delay (wait for on-chain confirmation)
      setTimeout(refreshActivity, 8000);
    } catch (err: any) {
      // Comp-code-specific error path. err.code is the RPC reason
      // (invalid_code | expired | mismatch | already_used | rate_limited).
      const friendly: Record<string, string> = {
        invalid_code: 'That code does not exist.',
        expired: 'That code has expired.',
        mismatch: 'That code is not for this customer or this shop.',
        already_used: 'That code has already been used.',
        rate_limited: 'Too many code attempts. Wait a minute and try again.',
      };
      const msg = friendly[err?.code] || `Approval failed${err?.code ? `: ${err.code}` : ''}`;
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally {
      setApprovingId(null);
    }
  }, [requestQuantities, compCodes, refreshActivity]);

  const handleDenyRequest = useCallback(async (request: StampRequestRow) => {
    const updated = await denyStampRequest(request.id);
    // Even when the deny "failed" (already expired/answered), the row is
    // resolved — drop it from the list either way so the card doesn't stick.
    setPendingRequests(prev => prev.filter(r => r.id !== request.id));
    if (!updated) {
      // eslint-disable-next-line no-alert
      alert('That request was already expired or answered.');
    }
  }, []);

  // handleApproveRedeem / handleDenyRedeem removed with the queue. The owner no
  // longer approves a redemption at all — the customer owns the RewardNFT, so
  // burning it was always their action, and the barista's tap is what performs
  // it. See docs/superpowers/specs/2026-07-29-reward-redemption-at-counter-design.md

  if (loading) {
    return (
      <OwnerLoadingShell active="counter" statusLabel="COUNTER" title="Loading…">
        <div className="bg-white rounded-[10px] p-8 text-center">
          <p className="font-heading text-[10px] text-body-grey animate-pulse">LOADING…</p>
        </div>
      </OwnerLoadingShell>
    );
  }

  if (!shopCapability) {
    return (
      <OwnerLoadingShell active="counter" statusLabel="COUNTER" title="No shop yet">
        <div className="bg-white rounded-[10px] p-8 text-center">
          <Coffee size={40} className="mx-auto mb-4 text-coral" />
          <p className="font-heading text-[10px] text-body-grey mb-4">NO SHOP REGISTERED</p>
          <p className="font-grotesk text-sm text-body-grey mb-6">
            Register your coffee shop to start accepting stamps.
          </p>
          <a
            href="/shops/register"
            className="inline-block rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6"
          >
            Register my shop
          </a>
        </div>
      </OwnerLoadingShell>
    );
  }

  // Header tally: STAMPED mirrors the APPROVED quick-stats tile below
  // (recentPurchases + this-session approvals) — existing dashboard state,
  // not a new fetch.
  // redeemed count omitted — backing state never wired (see fix/qa-sweep-2026-08-03)
  // The authoritative shop-scoped count, not `recentPurchases.length` — that
  // is a GLOBAL last-10 event page filtered to this shop client-side, so it
  // saturated at 10 and, once a second shop is live, could read 0 for a busy
  // café. Falls back to the page only until the real count has loaded.
  const stampedCount = totalStamps ?? recentPurchases.length;
  const pendingCount = pendingRequests.length;

  // Realtime connection state, surfaced through the header chip instead of
  // the old standalone banner (T13 restyle) — same wsState, same three
  // values (connected/connecting/disconnected), new location only.
  const liveChip = wsState === 'connected'
    ? { dot: 'bg-success', label: 'LIVE' }
    : wsState === 'connecting'
      ? { dot: 'bg-accent-alt animate-pulse', label: 'CONNECTING' }
      : { dot: 'bg-error', label: 'OFFLINE' };

  return (
    <OwnerShell
      active="counter"
      header={
        <CoralHeader
          statusLabel="COUNTER"
          eyebrow={`${stampedCount} STAMPED`}
          title={`${pendingCount} waiting`}
          chip={
            <div className="flex-none flex items-center gap-[7px] rounded-full bg-black/[.18] px-3.5 py-2 font-heading text-[8px] text-white">
              <span className={`w-2 h-2 rounded-full inline-block ${liveChip.dot}`} />
              {liveChip.label}
            </div>
          }
        />
      }
    >
      <div className="pb-8">
        {/* First-run setup checklist — self-hides once every step is done */}
        {dbShopId && (
          <OnboardingChecklist
            shopId={dbShopId}
            shop={shopData}
            squareConnected={squareStatus ? !!squareStatus.connected : null}
          />
        )}

        {/* Counter QR Code */}
        {activeShopOwner && (
          <div className="mb-8">
            <ShopQRCode
              shopOwnerAddress={activeShopOwner}
              shopName={shopData?.name || 'MY SHOP'}
            />
            {/* Pass scanner — stamp wallet-pass / cash customers with zero
                customer phone interaction (reads the pass QR). */}
            <div className="flex flex-wrap gap-2.5 mt-3.5">
              <button
                onClick={() => setScanOpen(true)}
                className="rounded-full bg-coral text-white font-grotesk font-bold text-[13px] py-3 px-4"
              >
                Scan member pass
              </button>
              {/* Counter Book — the zero-artifact rolodex tier: staff search
                  by name and tap to stamp/redeem, no card, no phone. */}
              <button
                onClick={() => setCounterBookOpen(true)}
                className="rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 px-4 hover:border-coral hover:text-coral"
              >
                Counter book
              </button>
              {/* Assisted-signup counter script (O4) — the ~2-min walkthrough
                  for app-averse regulars, ending in their paper card traded
                  into a wallet pass. */}
              <button
                onClick={() => navigate('/shops/assist')}
                className="rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 px-4 hover:border-coral hover:text-coral"
              >
                Sign up a regular
              </button>
            </div>
            <ScanPassModal open={scanOpen} onClose={() => setScanOpen(false)} />
            {dbShopId && (
              <CounterBook
                open={counterBookOpen}
                onClose={() => setCounterBookOpen(false)}
                shopId={dbShopId}
                // App customers can't be stamped from the book — only their own
                // device can sign the on-chain write — so tapping one hands off
                // to the scanner rather than dead-ending.
                onScanPass={() => { setCounterBookOpen(false); setScanOpen(true); }}
              />
            )}
          </div>
        )}

        {/* Billing — checkout activation banner. Sentence-case grotesk, not
            pixel font — these are narrative sentences, not status labels
            (T13 sweep; full plan-selection UI already lives in BillingCard
            under Setup → Account). */}
        {billingResult === 'pending' && (
          <p className="font-grotesk font-medium text-sm mb-3 text-pixel-cyan">
            Payment received — activating Pro…
          </p>
        )}
        {billingResult === 'activated' && (
          <p className="font-grotesk font-medium text-sm mb-3 text-success">
            Pro activated — welcome aboard!
          </p>
        )}

        {/* Reward-mirror staleness — the outage safety net has gone quiet */}
        {rewardMirrorStatus?.rewardMirrorStale && (
          <p className="font-grotesk font-medium text-sm mb-3 text-warning">
            ⚠ Reward verification mirror is stale — during an outage, redemptions will be refused rather than guessed.
          </p>
        )}

        {/* Square POS integration — mockup #8b's status card */}
        {activeShopOwner && (
          <div id="square-pos" className="bg-white rounded-[10px] p-[18px] mb-8">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h2 className="font-grotesk font-bold text-[17px] text-primary">Square POS</h2>
              {squareStatus?.connected && (
                <span className="rounded-full bg-success/[.14] text-[#2f9440] font-heading text-[8px] px-3 py-[7px]">
                  CONNECTED
                </span>
              )}
            </div>
            {squareResult && (
              <p
                className={`font-grotesk font-medium text-sm mb-3 ${
                  squareResult === 'connected' ? 'text-success' : 'text-error'
                }`}
              >
                {squareResult === 'connected' && 'Square connected — till payments now auto-confirm stamps.'}
                {squareResult === 'denied' && 'Connect cancelled on Square — nothing changed.'}
                {squareResult === 'badstate' && 'Connect link expired — hit connect Square again.'}
                {squareResult === 'error' && 'Square connect failed — try again in a minute.'}
              </p>
            )}
            {squareStatus?.connected ? (
              <div>
                <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey">
                  Purchases at your Square till auto-confirm stamps — customers tap in-app to claim.
                </p>
                <p className="font-heading text-[8px] text-[#c8c2b4] mt-2">
                  MERCHANT {squareStatus.merchant_id?.slice(0, 10)}…
                  {squareStatus.connected_at ? ` · SINCE ${squareStatus.connected_at.slice(0, 10)}` : ''}
                </p>
              </div>
            ) : (
              <div>
                <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey mb-3.5">
                  Connect Square so a real coffee purchase auto-approves the reward — no manual tap needed.
                </p>
                <button
                  onClick={handleSquareConnect}
                  disabled={squareBusy}
                  className="rounded-full bg-coral text-white font-grotesk font-bold text-[13px] py-3 px-4 disabled:opacity-50"
                >
                  {squareBusy ? 'Redirecting…' : 'Connect Square'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pending Stamp Requests — mockup #8a. Stepper (1-5), comp-code
            disclosure and the approve/deny handlers are unchanged; only the
            markup and classes below are new. */}
        <div className="mb-8">
          <p className="font-heading text-[9px] text-body-grey uppercase mb-3">
            STAMP REQUESTS{pendingRequests.length > 0 ? ` · ${pendingRequests.length} NEW` : ''}
          </p>

          {pendingRequests.length === 0 ? (
            <div className="bg-white rounded-[10px] p-8 text-center">
              <Bell size={28} className="mx-auto mb-3 text-hairline" />
              <p className="font-heading text-[10px] text-body-grey">NO PENDING REQUESTS</p>
              <p className="font-grotesk text-[13px] text-body-grey mt-2">
                Customers request stamps from the app.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingRequests.filter(r => r.status === 'pending').map((request) => {
                const minutesAgo = Math.floor((Date.now() - new Date(request.created_at).getTime()) / 60000);
                const qty = requestQuantities[request.id] || 1;
                const geoBadge = formatGeoBadge(request.geo_bucket, request.geo_distance_m);
                return (
                  <div key={request.id} className="bg-white rounded-[10px] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-grotesk font-bold text-[17px] text-primary truncate">
                          {request.customer_name || 'Customer'}
                        </p>
                        <p className="font-body text-[11px] text-[#a8a29e] mt-1">
                          {`${request.customer_address.slice(0, 10)}…${request.customer_address.slice(-6)}`}
                        </p>
                        <p className="font-grotesk font-medium text-[12px] text-[#a8a29e] mt-1">
                          {minutesAgo}m ago
                        </p>
                        {geoBadge && (
                          <span
                            className={`inline-block mt-2 px-2 py-1 rounded-full font-heading text-[9px] ${
                              geoBadge.tone === 'green'
                                ? 'text-success bg-success/10'
                                : 'text-gold-ink bg-accent-alt/15'
                            }`}
                          >
                            {geoBadge.label}
                          </span>
                        )}
                      </div>
                      <div className="flex-none text-right">
                        <p className="font-grotesk font-bold text-[20px] text-coral mb-2">+{qty * 10}</p>
                        <p className="font-heading text-[7px] text-body-grey mb-1">{currencyLabel(shopData)}</p>
                        {/* Quantity selector — capped 1-5, unchanged */}
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setRequestQuantities(prev => ({ ...prev, [request.id]: Math.max(1, qty - 1) }))}
                            disabled={qty <= 1}
                            aria-label="Decrease quantity"
                            className="w-8 h-8 border-2 border-hairline rounded-[8px] bg-white text-body-grey font-grotesk font-bold text-[15px] disabled:opacity-30"
                          >
                            −
                          </button>
                          <span className="font-grotesk font-bold text-[15px] text-primary w-3.5 text-center">{qty}</span>
                          <button
                            onClick={() => setRequestQuantities(prev => ({ ...prev, [request.id]: Math.min(5, qty + 1) }))}
                            disabled={qty >= 5}
                            aria-label="Increase quantity"
                            className="w-8 h-8 border-2 border-hairline rounded-[8px] bg-white text-body-grey font-grotesk font-bold text-[15px] disabled:opacity-30"
                          >
                            +
                          </button>
                        </div>
                        <p className="font-heading text-[7px] text-body-grey mt-2">COFFEES · MAX 5</p>
                      </div>
                    </div>

                    {/* Optional comp code — barista enters BREW-XXXXXXXX to redeem
                         a free coffee alongside the normal stamp approval. */}
                    <details className="mt-3.5">
                      <summary className="list-none cursor-pointer select-none bg-background rounded-[8px] px-3 py-2.5 flex items-center justify-between">
                        <span className="font-heading text-[8px] text-body-grey">+ COMP CODE</span>
                        <span className="font-body text-[11px] text-body-grey tracking-[2px]">BREW-XXXXXXXX</span>
                      </summary>
                      <input
                        type="text"
                        placeholder="BREW-XXXXXXXX"
                        value={compCodes[request.id] || ''}
                        onChange={(e) => setCompCodes(prev => ({ ...prev, [request.id]: e.target.value.toUpperCase() }))}
                        maxLength={13}
                        aria-label="Comp code"
                        className="mt-2 w-full bg-background rounded-[8px] px-3 py-2.5 font-body text-[13px] text-primary tracking-[2px] border-2 border-hairline focus:border-coral outline-none"
                      />
                    </details>

                    <div className="flex gap-2.5 mt-3.5">
                      <button
                        onClick={() => handleApproveRequest(request)}
                        disabled={approvingId === request.id}
                        className="flex-1 rounded-full bg-success text-white font-grotesk font-bold text-sm py-3 disabled:opacity-50"
                      >
                        {approvingId === request.id ? 'Approving…' : `Approve ×${qty}`}
                      </button>
                      <button
                        onClick={() => handleDenyRequest(request)}
                        disabled={approvingId === request.id}
                        className="rounded-full border-2 border-error text-error bg-transparent font-grotesk font-bold text-sm py-[11px] px-5 disabled:opacity-50"
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Item-box burns — the mockup's #8a shows this as an owner
            approve/deny queue, but that queue does not exist in current
            logic: burning a RewardNFT is the CUSTOMER's on-chain action
            (RewardCard → burnRedeemOnChain in Dashboard.tsx), settled by the
            barista's tap at redemption, not by an owner approval step here
            (see the removed handleApproveRedeem/handleDenyRedeem note and
            docs/superpowers/specs/2026-07-29-reward-redemption-at-counter-design.md).
            GiveawayStats is what actually lives on this page: a read-only
            record of completed burns, not a queue to work. Left as its own
            component/styling for now — folding it into the coral card
            language is tracked as a T13 follow-up (see task-13-report.md). */}
        {dbShopId && (
          <div className="mb-8">
            <GiveawayStats shopId={dbShopId} />
          </div>
        )}

        {/* Recent Activity — mockup #8a */}
        {recentPurchases.length > 0 && (
          <div>
            <p className="font-heading text-[9px] text-body-grey uppercase mb-3">RECENT ACTIVITY</p>
            <div className="bg-white rounded-[10px] p-4 flex flex-col gap-3">
              {recentPurchases
                .slice(0, 10)
                .map((e: any, index: number) => {
                  const d = e.parsedJson || {};
                  return (
                    <div key={index} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-body text-[11px] text-primary truncate">{d.customer?.slice(0, 12)}...</p>
                        <p className="font-grotesk font-medium text-[12px] text-[#a8a29e] mt-1">
                          {new Date(parseInt(d.timestamp) || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex-none text-right">
                        <p className="font-grotesk font-bold text-sm text-primary">+{parseInt(d.points_earned) || 10} {currencyLabel(shopData)}</p>
                        <p className="font-heading text-[7px] text-success mt-1">APPROVED</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </OwnerShell>
  );
}

export default ShopOwnerDashboard;
